import type { HTMLAttributes } from "react";
import { cn } from "../../lib/cn";

export type BadgeTone = "neutral" | "success" | "warn" | "danger" | "accent";
export type BadgeSize = "sm" | "md";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  size?: BadgeSize;
}

// Same ring treatment as StatusPill, so a tag and a status pill in adjacent columns look
// like one family rather than two badge systems.
const toneClasses: Record<BadgeTone, string> = {
  neutral: "bg-surface-hover text-text-muted ring-border",
  success: "bg-success-soft text-success ring-success/20",
  warn: "bg-warn-soft text-warn ring-warn/20",
  danger: "bg-danger-soft text-[var(--sb-danger-on-soft)] ring-danger/20",
  accent: "bg-accent-soft text-accent ring-accent/20",
};

const sizeClasses: Record<BadgeSize, string> = {
  sm: "h-6 px-2.5 text-xs",
  md: "h-7 px-3 text-sm",
};

export function Badge({ tone = "neutral", size = "sm", className, children, ...rest }: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-pill font-medium leading-none whitespace-nowrap",
        "ring-1 ring-inset",
        toneClasses[tone],
        sizeClasses[size],
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}
